import React, { useEffect, useState, useMemo } from 'react';
import { NextPageContext } from 'next';
import Head from 'next/head';

import { LocationCard } from '../../components';
import VillageFilters, { VillageFilterState } from '../../components/VillageFilters';
import { useSiteQuery } from '../../hooks';
import { sortLocations } from '../../lib/Distance';
import { matchesAreas } from '../../lib/AreaUtils';
import ErrorPage from '../_error';

import Page from '../[...all]';
import query from './index.graphql';
import styles from './index.module.scss';

interface Props {
  uri: string;
  search: string;
}

const renderCard = (item: LocationWithDistance) => {
  if (item?.location) {
    return <LocationCard key={item.location.uid!} item={item.location} />;
  }

  return null;
};

const RetirementVillagesLocations = ({ uri, search }: Props) => {
  const [{ data, error }] = useSiteQuery<VillageLocationsQuery>({ query });
  const [ordered, setOrdered] = useState<LocationWithDistance[]>([]);
  const [filters, setFilters] = useState<VillageFilterState>({
    type: null,
    availability: 'all',
    areas: [],
    postcode: search || '',
    sortBy: 'featured',
  });

  // Build a map of village IDs that have available units
  const villagesWithUnits = useMemo(() => {
    const unitMap = new Set<string>();
    if (data?.allUnits) {
      data.allUnits.forEach((unit: any) => {
        if (unit?.retirementVillageLocation && unit.retirementVillageLocation.length > 0) {
          unit.retirementVillageLocation.forEach((location: any) => {
            if (location?.id) {
              unitMap.add(location.id);
            }
          });
        }
      });
    }
    return unitMap;
  }, [data?.allUnits]);

  useEffect(() => {
    async function getSort() {
      if (data?.villageLocations) {
        const villageLocations: RetirementVillageLocationFragment[] = data.villageLocations.map(
          (item) => item as RetirementVillageLocationFragment,
        );
        const postcodeToUse = filters.postcode || search;
        const response = await sortLocations(villageLocations, postcodeToUse);
        setOrdered(response);
      }
    }
    getSort();
  }, [data, search, filters.postcode]);

  // Filter and sort the locations based on active filters
  const filteredLocations = useMemo(() => {
    let filtered = [...ordered];

    // Filter by type (sale/rent)
    // Assume for sale unless rentalOnly flag is true
    if (filters.type === 'rent') {
      filtered = filtered.filter((item) => {
        const rentalOnly = item.location && 'rentalOnly' in item.location ? item.location.rentalOnly : null;
        // Handle Radio Buttons field values from Craft CMS
        return rentalOnly === 'true' || rentalOnly === '1';
      });
    } else if (filters.type === 'sale') {
      filtered = filtered.filter((item) => {
        const rentalOnly = item.location && 'rentalOnly' in item.location ? item.location.rentalOnly : null;
        // Exclude rental only properties - only show if explicitly false or null
        return rentalOnly === 'false' || rentalOnly === null || rentalOnly === undefined;
      });
    }

    // Filter by availability - check if village has available units
    if (filters.availability === 'available') {
      filtered = filtered.filter((item) => {
        const locationId = item.location?.id;
        return locationId && villagesWithUnits.has(locationId);
      });
    }

    // Filter by area - use GPS coordinates to determine area
    if (filters.areas.length > 0) {
      filtered = filtered.filter((item) => {
        const lat = item.location?.address?.lat;
        const lng = item.location?.address?.lng;
        return matchesAreas(lat, lng, filters.areas);
      });
    }

    // Sort the results
    // Postcode/distance sorting takes priority over manual sort selection
    if (filters.postcode) {
      // When postcode is active, always sort by distance (handled by initial ordering)
      // Don't apply name sorting
    } else if (filters.sortBy === 'name-asc') {
      filtered.sort((a, b) => (a.location?.title || '').localeCompare(b.location?.title || ''));
    } else if (filters.sortBy === 'name-desc') {
      filtered.sort((a, b) => (b.location?.title || '').localeCompare(a.location?.title || ''));
    }
    // 'featured' and 'distance' sorting are handled by the initial ordering

    return filtered;
  }, [ordered, filters, villagesWithUnits]);

  const handleFilterChange = (newFilters: VillageFilterState) => {
    setFilters(newFilters);
  };

  if (error) {
    return <ErrorPage statusCode={500} err={error} />;
  }

  return (
    <>
      <Head>
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${
            process.env.NEXT_PUBLIC_GOOGLE_API_KEY || ''
          }&libraries=places`}
        />
      </Head>
      <Page uri={uri}>
        <div className="section">
          <div className="container">
            <div className="row">
              <div className={styles.cardIntro}>
                <p className={styles.preheading}>Villages</p>
                <h2>Explore Our Retirement Villages</h2>
                <p className={styles.subheading}>
                  Discover the perfect community for your retirement lifestyle.
                </p>
              </div>

              {ordered.length > 0 && (
                <VillageFilters
                  onFilterChange={handleFilterChange}
                  resultCount={filteredLocations.length}
                  totalCount={ordered.length}
                  sortedByDistance={!!filters.postcode}
                />
              )}

              {ordered.length === 0 ? null : filteredLocations.length === 0 ? (
                <div className={styles.noResults}>
                  <p>No matching results found</p>
                </div>
              ) : (
                <div className={`${styles.cardGrid} cardGrid`}>
                  {filteredLocations.map((item) => renderCard(item))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Page>
    </>
  );
};

RetirementVillagesLocations.getInitialProps = ({ req, query: { q } }: NextPageContext) => ({
  uri: req?.url?.replace(/^\//, '').replace(/\?.*$/, '') || '404',
  search: q! || '',
});

export default RetirementVillagesLocations;

